import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { warehouseAPI } from '../utils/api';
import { formatDateDMY } from '../utils/dateFormat';
import { useTheme } from '../contexts/ThemeContext';

const fmtNum = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const Warehouse = () => {
  const navigate = useNavigate();
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#E2E8F0' : '#1E293B' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#FFFFFF', borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, padding: 20 };
  const inp = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#E2E8F0' : '#1E293B', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const btnPrimary = { padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const btnSecondary = { padding: '8px 18px', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
  const lbl = { ...muted, fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' };

  const [tab, setTab] = useState('warehouses');
  const [warehouses, setWarehouses] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [selectedStock, setSelectedStock] = useState([]);
  const [selectedWH, setSelectedWH] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);

  const [form, setForm] = useState({ name: '', code: '', address: '', manager_name: '' });
  const [transferForm, setTransferForm] = useState({ from_warehouse_id: '', to_warehouse_id: '', notes: '', items: [{ product_id: '', product_name: '', quantity: 1 }] });

  const loadWarehouses = useCallback(async () => {
    try { const r = await warehouseAPI.list(); setWarehouses(Array.isArray(r.data) ? r.data : []); } catch (e) { console.error(e); }
  }, []);

  const loadTransfers = useCallback(async () => {
    try { const r = await warehouseAPI.listTransfers(); setTransfers(Array.isArray(r.data?.items) ? r.data.items : []); } catch (e) { console.error(e); }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    await loadWarehouses();
    if (tab === 'transfers') await loadTransfers();
    setLoading(false);
  }, [tab, loadWarehouses, loadTransfers]);

  useEffect(() => { loadData(); }, [loadData]);

  const viewStock = async (wh) => {
    setSelectedWH(wh);
    try { const r = await warehouseAPI.stock(wh.id); setSelectedStock(Array.isArray(r.data) ? r.data : []); } catch (e) { console.error(e); }
  };

  const createWarehouse = async (e) => {
    e.preventDefault();
    try { await warehouseAPI.create(form); setShowForm(false); setForm({ name: '', code: '', address: '', manager_name: '' }); loadWarehouses(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const createTransfer = async (e) => {
    e.preventDefault();
    try { await warehouseAPI.createTransfer(transferForm); setShowTransferForm(false); setTransferForm({ from_warehouse_id: '', to_warehouse_id: '', notes: '', items: [{ product_id: '', product_name: '', quantity: 1 }] }); loadTransfers(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const completeTransfer = async (id) => {
    try { await warehouseAPI.completeTransfer(id); loadTransfers(); } catch (e) { alert('Failed'); }
  };

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, marginBottom: 20 }}>🏭 Warehouse Management</h1>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {['warehouses', 'transfers'].map(t => (
              <button key={t} onClick={() => { setTab(t); setSelectedWH(null); }}
                style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${tab === t ? acc : (darkMode ? '#334155' : '#E2E8F0')}`, background: tab === t ? acc : 'transparent', color: tab === t ? '#fff' : pg.color, fontWeight: 600, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize' }}>
                {t}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => navigate('/stock-audit')}
            style={{ padding: '7px 16px', borderRadius: 8, border: `1.5px solid ${acc}`, background: 'transparent', color: acc, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span>📋</span> Physical Stock Audit
          </button>
        </div>

        {loading && <div style={{ ...muted, textAlign: 'center', padding: 40 }}>Loading...</div>}

        {/* Warehouses Tab */}
        {tab === 'warehouses' && !loading && (
          <div>
            <button onClick={() => setShowForm(!showForm)} style={{ ...btnPrimary, marginBottom: 16 }}>{showForm ? 'Cancel' : '+ New Warehouse'}</button>

            {showForm && (
              <form onSubmit={createWarehouse} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                  <div><label style={lbl}>Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} required /></div>
                  <div><label style={lbl}>Code</label><input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} style={inp} /></div>
                  <div><label style={lbl}>Address</label><input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} style={inp} /></div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}><button type="submit" style={btnPrimary}>Create</button></div>
                </div>
              </form>
            )}

            <div className="stagger-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {warehouses.map(wh => (
                <div key={wh.id} style={{ ...card, cursor: 'pointer', borderLeft: `4px solid ${acc}` }} onClick={() => viewStock(wh)}>
                  <div style={{ ...pg, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{wh.name}</div>
                  <div style={muted}>{wh.code || '—'} · {wh.address || 'No address'}</div>
                  {wh.is_primary && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: '#DBEAFE', color: '#1E40AF', fontWeight: 700, marginTop: 6, display: 'inline-block' }}>PRIMARY</span>}
                </div>
              ))}
            </div>

            {selectedWH && (
              <div style={{ ...card, marginTop: 20 }}>
                <h3 style={{ ...pg, fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Stock in: {selectedWH.name}</h3>
                {selectedStock.length === 0 ? (
                  <div style={{ ...muted, textAlign: 'center', padding: 20 }}>No stock in this warehouse</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                        {['Product', 'Quantity', 'Rack Location'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedStock.map((s, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                          <td style={{ padding: '8px 10px', ...pg }}>{s.product_name || s.product_id}</td>
                          <td style={{ padding: '8px 10px', ...pg, fontWeight: 700 }}>{fmtNum(s.quantity)}</td>
                          <td style={{ padding: '8px 10px', ...pg }}>{s.rack_location || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Transfers Tab */}
        {tab === 'transfers' && !loading && (
          <div>
            <button onClick={() => setShowTransferForm(!showTransferForm)} style={{ ...btnPrimary, marginBottom: 16 }}>{showTransferForm ? 'Cancel' : '+ New Transfer'}</button>

            {showTransferForm && (
              <form onSubmit={createTransfer} style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                  <div><label style={lbl}>From Warehouse</label>
                    <select value={transferForm.from_warehouse_id} onChange={e => setTransferForm(f => ({ ...f, from_warehouse_id: e.target.value }))} style={inp} required>
                      <option value="">Select</option>
                      {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                  <div><label style={lbl}>To Warehouse</label>
                    <select value={transferForm.to_warehouse_id} onChange={e => setTransferForm(f => ({ ...f, to_warehouse_id: e.target.value }))} style={inp} required>
                      <option value="">Select</option>
                      {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                  <div><label style={lbl}>Notes</label><input value={transferForm.notes} onChange={e => setTransferForm(f => ({ ...f, notes: e.target.value }))} style={inp} /></div>
                </div>
                <h4 style={{ ...pg, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Items</h4>
                {transferForm.items.map((item, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                    <div><label style={lbl}>Product ID</label><input value={item.product_id} onChange={e => { const items = [...transferForm.items]; items[idx].product_id = e.target.value; setTransferForm(f => ({ ...f, items })); }} style={inp} /></div>
                    <div><label style={lbl}>Product Name</label><input value={item.product_name} onChange={e => { const items = [...transferForm.items]; items[idx].product_name = e.target.value; setTransferForm(f => ({ ...f, items })); }} style={inp} /></div>
                    <div><label style={lbl}>Qty</label><input type="number" value={item.quantity} onChange={e => { const items = [...transferForm.items]; items[idx].quantity = Number(e.target.value); setTransferForm(f => ({ ...f, items })); }} style={inp} /></div>
                    {transferForm.items.length > 1 && <button type="button" onClick={() => setTransferForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} style={{ ...btnSecondary, padding: '8px 10px', color: '#EF4444' }}>✕</button>}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setTransferForm(f => ({ ...f, items: [...f.items, { product_id: '', product_name: '', quantity: 1 }] }))} style={btnSecondary}>+ Add Item</button>
                  <button type="submit" style={btnPrimary}>Create Transfer</button>
                </div>
              </form>
            )}

            {transfers.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No stock transfers yet</div>
            ) : (
              <div style={card}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['Transfer #', 'From', 'To', 'Status', 'Date', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', ...muted, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transfers.map(t => (
                      <tr key={t.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '8px 10px', ...pg, fontWeight: 600 }}>{t.transfer_number}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{t.from_warehouse || t.from_warehouse_id}</td>
                        <td style={{ padding: '8px 10px', ...pg }}>{t.to_warehouse || t.to_warehouse_id}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: t.status === 'completed' ? '#DCFCE7' : '#FEF3C7', color: t.status === 'completed' ? '#166534' : '#92400E' }}>
                            {t.status?.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', ...pg }}>{formatDateDMY(t.created_at)}</td>
                        <td style={{ padding: '8px 10px' }}>
                          {t.status === 'pending' && <button onClick={() => completeTransfer(t.id)} style={{ ...btnPrimary, padding: '4px 12px', fontSize: 11 }}>Complete</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Warehouse;
